<template>
    <div class="pst" :class="{'is-overflowing': overflowing, 'is-at-end': atEnd}"
        ref="box" :tabindex="overflowing ? 0 : null"
        :style="overflowing ? {maxHeight: `${fullHeight}px`} : null"
        :title="overflowing ? 'Arraste o canto inferior para esticar' : null"
        @scroll.passive="checkEnd">
        <div ref="content"><slot /></div>
    </div>
</template>

<script>
// Texto longo com altura limitada: a rolagem fica no próprio texto, não na página.
// O canto inferior pode ser arrastado para esticar, até o tamanho do texto inteiro.
const COLLAPSED_LINES_EM = 7.5
export default {
    name: 'ProductScrollTextCmp',
    data() {
        return {
            overflowing: false,
            atEnd: false,
            fullHeight: 0,
        }
    },
    mounted() {
        this.measure()
        window.addEventListener('resize', this.measure)

        // Ao esticar a caixa, o esmaecido do fim precisa acompanhar
        if (window.ResizeObserver) {
            this.resizeObserver = new ResizeObserver(() => this.checkEnd())
            this.resizeObserver.observe(this.$refs.box)
        }
    },
    updated() {
        this.measure()
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.measure)

        if (this.resizeObserver) {
            this.resizeObserver.disconnect()
        }
    },
    methods: {
        measure() {
            const box = this.$refs.box

            if (!box) {
                return
            }

            // Mede o texto em si, e não a caixa: assim continua "longo" mesmo depois de esticado
            const style = window.getComputedStyle(box)
            const collapsed = parseFloat(style.fontSize) * COLLAPSED_LINES_EM
            const contentHeight = this.$refs.content.offsetHeight
            const overflowing = contentHeight - collapsed > 1
            const chrome = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom)
                + parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth)
            const fullHeight = Math.ceil(contentHeight + chrome)

            if (overflowing !== this.overflowing) {
                this.overflowing = overflowing
            }

            if (fullHeight !== this.fullHeight) {
                this.fullHeight = fullHeight
            }

            this.checkEnd()
        },
        checkEnd() {
            const box = this.$refs.box

            if (!box) {
                return
            }

            const atEnd = box.scrollTop + box.clientHeight >= box.scrollHeight - 2

            if (atEnd !== this.atEnd) {
                this.atEnd = atEnd
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.pst {
    max-height: 7.5em;
    overflow-y: auto;
    overscroll-behavior: contain;
    line-height: 1.5;
    font-weight: 500;
    overflow-wrap: anywhere;
    scrollbar-width: thin;
    scrollbar-color: var(--border-strong) transparent;

    &::-webkit-scrollbar {
        width: 6px;
    }

    &::-webkit-scrollbar-thumb {
        border-radius: 999px;
        background: var(--border-strong);
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }

    &.is-overflowing {
        // Mesma quantidade de texto visível de antes, agora com o padding da caixa
        height: calc(7.5em + 18px);
        min-height: calc(3em + 18px);
        resize: vertical;
        padding: 8px 10px 8px 12px;
        border: 1px solid var(--border-subtle);
        border-radius: 10px;
        background: var(--surface-2);
        // Esmaece o fim enquanto ainda há texto abaixo, sem cobrir a barra
        &::after {
            content: '';
            position: sticky;
            bottom: -8px;
            display: block;
            height: 26px;
            margin: -26px -10px -8px -12px;
            background: linear-gradient(to bottom, transparent, var(--surface-2));
            pointer-events: none;
            transition: opacity .2s;
        }

        &:focus-visible {
            outline: 2px solid var(--brand-soft);
            outline-offset: 2px;
        }
    }

    &.is-overflowing.is-at-end::after {
        opacity: 0;
    }
}
</style>
